/**
 * Banking generation functions for seed / mock data
 * @module banking/generation
 */

import { IBANBuilder, CountryCode } from 'ibankit';
import { CZECH_BANK_CODES } from './czech-bank-codes';

// ---------------------------------------------------------------------------
// IBAN generation
// ---------------------------------------------------------------------------

/**
 * Generate a valid IBAN from country code, bank code, and account number.
 * Check digits are computed automatically by ibankit.
 *
 * Some countries (e.g. CZ) use branchCode in their BBAN structure.
 * For CZ, ibankit maps the 6-digit account prefix to branchCode.
 */
export function generateIBAN(
  countryCode: CountryCode,
  bankCode: string,
  accountNumber: string,
  branchCode?: string
): string {
  const builder = new IBANBuilder()
    .countryCode(countryCode)
    .bankCode(bankCode)
    .accountNumber(accountNumber);

  if (branchCode !== undefined) {
    builder.branchCode(branchCode);
  }

  return builder.build().toString();
}

/**
 * Generate a random valid Czech IBAN.
 * Optionally specify a bank code (must be a known 4-digit code).
 */
export function generateRandomCzechIBAN(bankCode?: string): string {
  const code = bankCode ?? randomCzechBankCode();
  const accountNumber = generateMod11Number(10);
  const prefix = generateMod11Number(6);

  return generateIBAN(CountryCode.CZ, code, accountNumber, prefix);
}

// ---------------------------------------------------------------------------
// Czech account number generation
// ---------------------------------------------------------------------------

/**
 * Generate a random Czech bank account number (without prefix).
 * Returns format: "accountNumber/bankCode"
 */
export function generateRandomCzechAccountNumber(bankCode?: string): string {
  const code = bankCode ?? randomCzechBankCode();
  const accountNumber = generateMod11Number(10);
  return `${accountNumber}/${code}`;
}

/**
 * Generate a random Czech bank account with optional prefix.
 * Returns format: "prefix-accountNumber/bankCode" or "accountNumber/bankCode"
 */
export function generateRandomCzechBankAccount(bankCode?: string): string {
  const code = bankCode ?? randomCzechBankCode();
  const accountNumber = generateMod11Number(10);
  const usePrefix = Math.random() > 0.5;

  if (usePrefix) {
    const prefix = generateMod11Number(6);
    return `${prefix}-${accountNumber}/${code}`;
  }

  return `${accountNumber}/${code}`;
}

// Re-export CountryCode for convenience
export { CountryCode } from 'ibankit';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function randomCzechBankCode(): string {
  const codes = Object.keys(CZECH_BANK_CODES);
  return codes[Math.floor(Math.random() * codes.length)];
}

const MOD11_WEIGHTS = [6, 3, 7, 9, 10, 5, 8, 4, 2, 1] as const;

/**
 * Generate a random number string of given length that passes mod11 check.
 * Strategy: generate first (length-1) digits randomly, compute last digit
 * so that the weighted sum is divisible by 11.
 */
function generateMod11Number(length: number): string {
  const digits = Array.from({ length: length - 1 }, () => Math.floor(Math.random() * 10));

  // Pad to 10 positions for weight alignment (right-aligned)
  const padded = new Array(10 - length).fill(0).concat(digits, [0]);

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += padded[i] * MOD11_WEIGHTS[i];
  }

  // Last weight is 1, so we need lastDigit such that (sum + lastDigit) % 11 === 0
  const lastDigit = (11 - (sum % 11)) % 11;

  // If lastDigit is 10, it's impossible with a single digit — regenerate
  if (lastDigit === 10) {
    return generateMod11Number(length);
  }

  digits.push(lastDigit);
  return digits.join('');
}
